import * as THREE from '../three'

import { System } from './System'

const up = new THREE.Vector3(0, 1, 0)
const v1 = new THREE.Vector3()

export class AudioSystem extends System {
  constructor(client) {
    super(client)
    this.handles = new Set()
    this.ctx = new AudioContext() // new (window.AudioContext || window.webkitAudioContext)();
    this.masterGain = this.ctx.createGain()
    this.masterGain.connect(this.ctx.destination)
    this.groupGains = {
      music: this.ctx.createGain(),
      sfx: this.ctx.createGain(),
      voice: this.ctx.createGain(),
    }
    this.groupGains.music.gain.value = client.prefs.music
    this.groupGains.sfx.gain.value = client.prefs.sfx
    this.groupGains.voice.gain.value = client.prefs.voice
    this.groupGains.music.connect(this.masterGain)
    this.groupGains.sfx.connect(this.masterGain)
    this.groupGains.voice.connect(this.masterGain)
    this.listener = this.ctx.listener
    this.listener.positionX.value = 0
    this.listener.positionY.value = 0
    this.listener.positionZ.value = 0
    this.listener.forwardX.value = 0
    this.listener.forwardY.value = 0
    this.listener.forwardZ.value = -1
    this.listener.upX.value = 0
    this.listener.upY.value = 1
    this.listener.upZ.value = 0
    this.lastDelta = 0

    this.queue = []
    this.unlocked = this.ctx.state !== 'suspended'
    if (!this.unlocked) {
      this.setupUnlockListener()
    }
  }

  ready(fn) {
    if (this.unlocked) return fn()
    this.queue.push(fn)
  }

  setupUnlockListener() {
    const complete = () => {
      this.unlocked = true
      document.removeEventListener('click', unlock)
      document.removeEventListener('touchstart', unlock)
      document.removeEventListener('keydown', unlock)
      while (this.queue.length) {
        this.queue.pop()()
      }
      console.log('[audio] unlocked')
    }
    const unlock = async () => {
      try {
        await this.ctx.resume()
        if (this.ctx.state !== 'running') throw new Error('Audio still suspended')
        const video = document.createElement('video')
        video.playsInline = true
        video.muted = true
        video.src = '/tiny.mp4'
        video
          .play()
          .then(() => {
            video.pause()
            video.remove()
            console.log('[audio] video played')
          })
          .catch(err => {
            console.log('[audio] video failed')
          })
      } catch (err) {
        console.error(err)
      } finally {
        // either way, mark the system as unlocked
        complete()
      }
    }
    document.addEventListener('click', unlock)
    document.addEventListener('touchstart', unlock)
    document.addEventListener('keydown', unlock)
    console.log('[audio] suspended, waiting for interact...')
  }

  async init() {
    this.client.prefs.on('change', this.onPrefsChange)
    this.sounds = new SoundPlayer(this.ctx, this.client)
  }

  start() {
    // ...
  }

  lateUpdate(delta) {
    const target = this.client.entities.player?.head
    if (!target) return
    const dir = v1.set(0, 0, -1).applyQuaternion(target.quaternion)
    if (this.listener.positionX) {
      // https://github.com/mrdoob/three.js/blob/master/src/audio/AudioListener.js
      // code path for Chrome (see three#14393)
      const endTime = this.ctx.currentTime + delta * 2
      this.listener.positionX.linearRampToValueAtTime(target.position.x, endTime)
      this.listener.positionY.linearRampToValueAtTime(target.position.y, endTime)
      this.listener.positionZ.linearRampToValueAtTime(target.position.z, endTime)
      this.listener.forwardX.linearRampToValueAtTime(dir.x, endTime)
      this.listener.forwardY.linearRampToValueAtTime(dir.y, endTime)
      this.listener.forwardZ.linearRampToValueAtTime(dir.z, endTime)
      this.listener.upX.linearRampToValueAtTime(up.x, endTime)
      this.listener.upY.linearRampToValueAtTime(up.y, endTime)
      this.listener.upZ.linearRampToValueAtTime(up.z, endTime)
    } else {
      this.listener.setPosition(target.position.x, target.position.y, target.position.z)
      this.listener.setOrientation(dir.x, dir.y, dir.z, up.x, up.y, up.z)
    }
    this.lastDelta = delta * 2
  }

  onPrefsChange = changes => {
    if (changes.music) {
      this.groupGains.music.gain.value = changes.music.value
    }
    if (changes.sfx) {
      this.groupGains.sfx.gain.value = changes.sfx.value
    }
    if (changes.voice) {
      this.groupGains.voice.gain.value = changes.voice.value
    }
  }

  destroy() {
    this.groupGains.music.disconnect()
    this.groupGains.sfx.disconnect()
    this.groupGains.voice.disconnect()
    this.masterGain.disconnect()
    this.ctx.close()
    this.handles.clear()
    this.queue = []
  }
}

class SoundPlayer {
  constructor(ctx, client) {
    this.ctx = ctx
    this.client = client
    this.bufferCache = new Map()
    this.nodePool = []
  }

  async getSound(id) {
    let audioBuffer = this.bufferCache.get(id)
    if (!audioBuffer) {
      const uint8Array = this.client.registry.get('audio', id)?.data
      if (!uint8Array) return null
      const arrayBuffer = uint8Array.buffer.slice(uint8Array.byteOffset, uint8Array.byteOffset + uint8Array.byteLength)
      audioBuffer = await this.ctx.decodeAudioData(arrayBuffer)
      this.bufferCache.set(id, audioBuffer)
    }
    return audioBuffer
  }

  acquireNodes(position, volume, group) {
    let nodes = this.nodePool.pop()

    if (!nodes) {
      const panner = this.ctx.createPanner()
      panner.panningModel = 'HRTF'
      panner.distanceModel = 'inverse'
      panner.refDistance = 1
      panner.maxDistance = 50
      panner.rolloffFactor = 1

      const gain = this.ctx.createGain()
      nodes = { panner, gain }
    }

    const groupGain = this.client.audio.groupGains[group]

    if (position) {
      nodes.panner.positionX.value = position.x
      nodes.panner.positionY.value = position.y
      nodes.panner.positionZ.value = position.z
      nodes.gain.connect(nodes.panner)
      nodes.panner.connect(groupGain)
    } else {
      nodes.gain.connect(groupGain)
    }

    nodes.gain.gain.value = volume
    nodes.spatial = !!position

    return nodes
  }

  releaseNodes(nodes) {
    nodes.gain.disconnect()
    if (nodes.spatial) {
      nodes.panner.disconnect()
    }
    this.nodePool.push(nodes)
  }

  play({ id, volume = 1, pitch = 1, position = null, loop = false, group = 'sfx' }) {
    const nodes = this.acquireNodes(position, volume, group)
    const source = this.ctx.createBufferSource()
    source.playbackRate.value = pitch
    source.loop = loop
    source.connect(nodes.gain)

    let dead = false
    let started = false

    this.getSound(id).then(buffer => {
      if (!buffer || dead) {
        this.releaseNodes(nodes)
        return
      }
      source.buffer = buffer
      source.start()
      started = true
    })

    const handle = {
      stop: (fadeTime = 0.1) => {
        if (dead) return
        dead = true
        nodes.gain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + fadeTime)
        if (started) {
          source.stop(this.ctx.currentTime + fadeTime)
        }
        setTimeout(
          () => {
            source.disconnect()
            this.releaseNodes(nodes)
          },
          fadeTime * 1000 + 50
        )
      },
      setVolume: v => {
        if (dead) return
        nodes.gain.gain.linearRampToValueAtTime(v, this.ctx.currentTime + 0.1)
      },
      setPosition: pos => {
        if (dead || !nodes.spatial) return
        nodes.panner.positionX.value = pos.x
        nodes.panner.positionY.value = pos.y
        nodes.panner.positionZ.value = pos.z
      },
    }

    if (!loop) {
      source.onended = () => {
        if (dead) return
        dead = true
        source.disconnect()
        this.releaseNodes(nodes)
      }
    }

    return handle
  }
}
